import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ProcessingStatus({ data, onRefresh }) {
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchSize, setBatchSize] = useState(50);
  const [maxBatches, setMaxBatches] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [batchError, setBatchError] = useState(null);

  if (!data || !data.processing_status) {
    return null;
  }

  const { processing_status } = data;
  const {
    total_transactions,
    processed,
    pending,
    pre_anchor_unbooked,
    recent_activity
  } = processing_status;
  const unclassifiedPending = data.data_quality?.unclassified_transactions || 0;

  const handleRunAI = () => {
    setBatchDialogOpen(true);
    setBatchResult(null);
    setBatchError(null);
  };

  const handleStartProcessing = async () => {
    setProcessing(true);
    setBatchError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accounting/classification/run-batch`,
        {
          batchSize: parseInt(batchSize),
          maxBatches: parseInt(maxBatches)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setBatchResult(response.data.data);
        // Refresh parent data after delay
        setTimeout(() => {
          if (onRefresh) onRefresh();
        }, 2000);
      }
    } catch (error) {
      console.error('Error running AI batch:', error);
      setBatchError(error.response?.data?.error?.message || 'Failed to run AI classification');
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseBatchDialog = () => {
    setBatchDialogOpen(false);
    setBatchSize(50);
    setMaxBatches(1);
    setBatchResult(null);
    setBatchError(null);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  return (
    <>
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box mb={3}>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          Ledger Booking Status
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Every bank/gateway money movement, measured against the ledger — booked means
          linked to a live journal entry. The backlog counts unbooked items since the
          2025-01-01 ledger anchor; older unbooked history is carried by the anchored
          2024-12-31 opening balances and does not affect whether the books balance.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* All money movements (all time) */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2.5,
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
              borderLeft: '4px solid #2196f3'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <HourglassIcon sx={{ fontSize: 20, color: '#2196f3' }} />
              <Typography variant="body2" color="text.secondary">
                Money Movements (all time)
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="700" color="text.primary">
              {formatNumber(total_transactions)}
            </Typography>
          </Box>
        </Grid>

        {/* Booked */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2.5,
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
              borderLeft: '4px solid #4caf50'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <CheckCircleIcon sx={{ fontSize: 20, color: '#4caf50' }} />
              <Typography variant="body2" color="text.secondary">
                Booked to Ledger
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="700" color="text.primary">
              {formatNumber(processed)}
            </Typography>
          </Box>
        </Grid>

        {/* Actionable backlog since the ledger anchor */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2.5,
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
              borderLeft: '4px solid #ff9800'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <ScheduleIcon sx={{ fontSize: 20, color: '#ff9800' }} />
              <Typography variant="body2" color="text.secondary">
                Actionable Backlog (2025+)
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="700" color="text.primary">
              {formatNumber(pending)}
            </Typography>
          </Box>
        </Grid>

        {/* Pre-anchor unbooked history */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2.5,
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
              borderLeft: '4px solid #9e9e9e'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <HourglassIcon sx={{ fontSize: 20, color: '#9e9e9e' }} />
              <Typography variant="body2" color="text.secondary">
                Pre-2025 Unbooked (historical)
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="700" color="text.primary">
              {formatNumber(pre_anchor_unbooked)}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      {recent_activity && (
        <Box mt={3}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <TrendingUpIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
              Recent Activity
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                  borderRadius: 2,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h5" fontWeight="700" color="primary">
                  {formatNumber(recent_activity.last_24h)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last 24 Hours
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(76, 175, 80, 0.08)',
                  borderRadius: 2,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h5" fontWeight="700" color="success.main">
                  {formatNumber(recent_activity.last_7d)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last 7 Days
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(255, 152, 0, 0.08)',
                  borderRadius: 2,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h5" fontWeight="700" color="warning.main">
                  {formatNumber(recent_activity.last_30d)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last 30 Days
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* AI Batch Processing Button — count = unclassified rows in the actionable backlog */}
      {unclassifiedPending > 0 && (
        <Box mt={3} display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleRunAI}
            size="large"
          >
            Run AI Classification ({formatNumber(unclassifiedPending)} unclassified)
          </Button>
        </Box>
      )}
    </Paper>

    {/* AI Batch Configuration Dialog */}
    <Dialog open={batchDialogOpen} onClose={handleCloseBatchDialog} maxWidth="sm" fullWidth>
      <DialogTitle>AI Classification Batch Processing</DialogTitle>

      <DialogContent>
        {batchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {batchError}
          </Alert>
        )}

        {batchResult ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="600">
              Batch Processing Complete!
            </Typography>
            <Box mt={1}>
              <Typography variant="body2">
                • Processed: {batchResult.processed || 0} transactions
              </Typography>
              <Typography variant="body2">
                • Classified: {batchResult.classified || 0} ({batchResult.avgConfidence || 0}% avg confidence)
              </Typography>
              {batchResult.failed > 0 && (
                <Typography variant="body2" color="error">
                  • Failed: {batchResult.failed}
                </Typography>
              )}
            </Box>
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Configure batch processing settings. The AI will analyze transaction descriptions and suggest GL account classifications.
            </Typography>

            <TextField
              label="Batch Size"
              type="number"
              value={batchSize}
              onChange={(e) => setBatchSize(Math.max(1, Math.min(200, parseInt(e.target.value) || 50)))}
              fullWidth
              helperText="Number of transactions per batch (1-200)"
              sx={{ mb: 2 }}
              disabled={processing}
            />

            <TextField
              label="Max Batches"
              type="number"
              value={maxBatches}
              onChange={(e) => setMaxBatches(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
              fullWidth
              helperText="Maximum number of batches to process (1-10)"
              sx={{ mb: 2 }}
              disabled={processing}
            />

            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="600">
                Estimated Processing
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                • Total transactions: {batchSize * maxBatches}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Estimated time: ~{Math.ceil((batchSize * maxBatches) / 25)} minutes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Estimated cost: ~${((batchSize * maxBatches * 0.003).toFixed(2))} (Claude API)
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseBatchDialog} disabled={processing}>
          {batchResult ? 'Close' : 'Cancel'}
        </Button>
        {!batchResult && (
          <Button
            onClick={handleStartProcessing}
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          >
            {processing ? 'Processing...' : 'Start Processing'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </>
  );
}
